//src/pages/program/SessionPlayerPage.jsx
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { api } from '../../services/api';
import { getPhaseData } from '../../data/programData';
import { getSupersets, getWarmupData } from '../../data/trainingSessionHelpers';
import { ArrowLeft, X, AlertCircle } from 'lucide-react';
import { Button } from '../../components/ui';
import { WarmupPlayer } from '../../components/training/WarmupPlayer';
import { SupersetPlayer } from '../../components/training/SupersetPlayer';
import { SessionComplete } from '../../components/training/SessionComplete';
import { SESSION_STORAGE_KEY, incrementSessionCompletion } from '../../utils/sessionCompletion';

export function SessionPlayerPage() {
  const { phaseId, sessionNum } = useParams();
  const navigate = useNavigate();
  const { aclCare, updateAclCare, user } = useAuth();

  const [sessionState, setSessionState] = useState(null);
  const [showExitConfirm, setShowExitConfirm] = useState(false);
  const [error, setError] = useState(null);

  const phaseNum = parseInt(phaseId);
  const workoutNum = parseInt(sessionNum);

  const phaseData = getPhaseData(phaseNum);
  const supersets = getSupersets(phaseNum, workoutNum);
  const warmupData = getWarmupData(phaseNum);

  // Load session state on mount
  useEffect(() => {
    const stored = localStorage.getItem(SESSION_STORAGE_KEY);

    if (stored) {
      try {
        const parsed = JSON.parse(stored);

        if (parsed.phaseNum === phaseNum && parsed.workoutNum === workoutNum) {
          setSessionState(parsed);
        } else {
          initializeSession();
        }
      } catch (e) {
        setError('Failed to load session data');
        initializeSession();
      }
    } else {
      initializeSession();
    }
  }, [phaseNum, workoutNum]);

  const initializeSession = () => {
    const newSession = {
      date: new Date().toDateString(),
      phaseNum,
      workoutNum,
      phase: 'warmup',
      warmupCompleted: false,
      currentWarmupCategoryIndex: 0,
      currentWarmupExerciseIndex: 0,
      currentSupersetIndex: 0,
      completedSupersets: [],
      setData: {},
      completed: false,
      startedAt: new Date().toISOString(),
    };
    setSessionState(newSession);
    localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(newSession));
  };

  const saveSessionState = (updates) => {
    const newState = { ...sessionState, ...updates };
    setSessionState(newState);
    localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(newState));
  };

  // Warmup handlers
  const handleWarmupExerciseComplete = (categoryIndex, exerciseIndex) => {
    saveSessionState({
      currentWarmupCategoryIndex: categoryIndex,
      currentWarmupExerciseIndex: exerciseIndex,
    });
  };

  const handleWarmupSkipCategory = (categoryIndex, exerciseIndex) => {
    saveSessionState({
      currentWarmupCategoryIndex: categoryIndex,
      currentWarmupExerciseIndex: exerciseIndex,
    });
  };

  const handleWarmupComplete = () => {
    saveSessionState({
      warmupCompleted: true,
      phase: 'supersets',
      currentSupersetIndex: 0,
    });
  };

  // Superset handlers
  const handleSupersetComplete = (supersetId, setData) => {

    const newCompletedSupersets = [...(sessionState.completedSupersets || [])];
    if (!newCompletedSupersets.includes(supersetId)) {
      newCompletedSupersets.push(supersetId);
    }

    const newSetData = {
      ...(sessionState.setData || {}),
      [supersetId]: setData,
    };

    const nextIndex = sessionState.currentSupersetIndex + 1;

    if (nextIndex >= supersets.length) {
      saveSessionState({
        completedSupersets: newCompletedSupersets,
        setData: newSetData,
        phase: 'complete',
        completed: true,
        completedAt: new Date().toISOString(),
      });
    } else {
      saveSessionState({
        completedSupersets: newCompletedSupersets,
        setData: newSetData,
        currentSupersetIndex: nextIndex,
      });
    }
  };

  const handleSupersetSkip = () => {
    const nextIndex = sessionState.currentSupersetIndex + 1;

    if (nextIndex >= supersets.length) {
      saveSessionState({
        phase: 'complete',
        completed: true,
        completedAt: new Date().toISOString(),
      });
    } else {
      saveSessionState({
        currentSupersetIndex: nextIndex,
      });
    }
  };

  const handleNavigateSuperset = (index) => {
    if (index >= 0 && index < supersets.length) {
      saveSessionState({
        currentSupersetIndex: index,
      });
    }
  };

  // Build payload for API
  const buildTrainingSessionPayload = () => {
    const startTime = new Date(sessionState.startedAt);
    const endTime = sessionState.completedAt ? new Date(sessionState.completedAt) : new Date();
    const durationMinutes = Math.round((endTime - startTime) / 60000);

    // Build supersets data with exercise IDs and set data
    const supersetsData = supersets.map((superset, index) => {
      const setData = sessionState.setData?.[superset.id] || {};
      const isCompleted = sessionState.completedSupersets?.includes(superset.id);

      return {
        superset_id: superset.id,
        sequence: index,
        completed: isCompleted,
        exercise1_id: `${superset.id}_ex1`,
        exercise1_name: superset.exercise1.name,
        exercise2_id: `${superset.id}_ex2`,
        exercise2_name: superset.exercise2.name,
        sets: {
          set1: setData.set1 || null,
          set2: setData.set2 || null,
          set3: setData.set3 || null,
        }
      };
    });

    return {
      user_id: user.id,
      phase: phaseNum,
      workout: workoutNum,
      session_type: 'training',
      started_at: sessionState.startedAt,
      completed_at: sessionState.completedAt || new Date().toISOString(),
      duration_minutes: durationMinutes,
      warmup_completed: sessionState.warmupCompleted,
      exercises: {
        supersets: supersetsData,
      }
    };
  };

  // Session complete handler
  const handleSessionFinish = async () => {
    try {
      // Build the training session payload
      const payload = buildTrainingSessionPayload();

      // Create training session record
      const result = await api.createTrainingSession(payload);

      // Update user's acl_care completions
      await updateAclCare(incrementSessionCompletion(aclCare, phaseNum, workoutNum));

      // Clear session storage
      localStorage.removeItem(SESSION_STORAGE_KEY);

      // Navigate back to training sessions. Replace the player entry in
      // history so the browser back button doesn't drop the user into a
      // fresh, empty player for the workout they just finished.
      navigate(`/program/phase/${phaseId}/training`, { replace: true });
    } catch (err) {
      console.error('[SessionPlayerPage] Error saving session completion:', err);
      setError('Failed to save session. Please try again.');
    }
  };

  const handleExit = () => {
    setShowExitConfirm(true);
  };

  const confirmExit = () => {
    navigate(`/program/phase/${phaseId}/training/session/${sessionNum}`);
  };

  // Loading state
  if (!sessionState) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-500">Loading session...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="text-center max-w-md">
          <div className="h-16 w-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="h-8 w-8 text-red-600" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Something went wrong</h2>
          <p className="text-gray-500 mb-6">{error}</p>
          <Button onClick={() => navigate(`/program/phase/${phaseId}/training`)}>
            Back to Training
          </Button>
        </div>
      </div>
    );
  }

  // Missing data state
  if (!warmupData && sessionState.phase === 'warmup') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="text-center max-w-md">
          <div className="h-16 w-16 rounded-full bg-yellow-100 flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="h-8 w-8 text-yellow-600" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">No warmup data</h2>
          <p className="text-gray-500 mb-6">Warmup exercises haven't been configured for this phase yet.</p>
          <Button onClick={() => navigate(`/program/phase/${phaseId}/training`)}>
            Back to Training
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between flex-shrink-0">
        <button
          onClick={handleExit}
          className="p-2 -ml-2 rounded-full hover:bg-gray-100"
        >
          <ArrowLeft className="h-5 w-5 text-gray-600" />
        </button>

        <h1 className="font-semibold text-gray-900">
          {sessionState.phase === 'warmup' && 'Warmup'}
          {sessionState.phase === 'supersets' && `Superset ${sessionState.currentSupersetIndex + 1}/${supersets.length}`}
          {sessionState.phase === 'complete' && 'Complete!'}
        </h1>

        <button
          onClick={handleExit}
          className="p-2 -mr-2 rounded-full hover:bg-gray-100"
        >
          <X className="h-5 w-5 text-gray-600" />
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {sessionState.phase === 'warmup' && warmupData && (
          <WarmupPlayer
            warmupData={warmupData}
            currentCategoryIndex={sessionState.currentWarmupCategoryIndex}
            currentExerciseIndex={sessionState.currentWarmupExerciseIndex}
            onExerciseComplete={handleWarmupExerciseComplete}
            onSkipCategory={handleWarmupSkipCategory}
            onWarmupComplete={handleWarmupComplete}
            onExit={handleExit}
          />
        )}

        {sessionState.phase === 'supersets' && (
          <SupersetPlayer
            phaseNum={phaseNum}
            supersets={supersets}
            currentIndex={sessionState.currentSupersetIndex}
            completedSupersets={sessionState.completedSupersets || []}
            existingSetData={sessionState.setData || {}}
            onSupersetComplete={handleSupersetComplete}
            onSupersetSkip={handleSupersetSkip}
            onNavigate={handleNavigateSuperset}
            onExit={handleExit}
          />
        )}

        {sessionState.phase === 'complete' && (
          <SessionComplete
            phaseNum={phaseNum}
            workoutNum={workoutNum}
            sessionState={sessionState}
            supersets={supersets}
            onFinish={handleSessionFinish}
          />
        )}
      </div>

      {/* Exit Confirmation Modal */}
      {showExitConfirm && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="fixed inset-0 bg-black/50" />

          <div className="flex min-h-full items-end justify-center p-4 sm:items-center">
            <div className="relative bg-white rounded-t-xl sm:rounded-xl shadow-xl w-full max-w-md p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-2">
                Exit Session?
              </h2>
              <p className="text-gray-600 mb-6">
                Your progress will be saved and you can resume later today.
              </p>

              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setShowExitConfirm(false)}
                  fullWidth
                >
                  Continue Workout
                </Button>
                <Button
                  onClick={confirmExit}
                  fullWidth
                >
                  Exit & Save
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}